import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { CondicoesIntervTableRowV2 } from './CondicoesIntervTableRowV2';
import { Ci2HelpHexIcon } from './Ci2HelpHexIcon';
import {
  CAMPOS_INTERV_PREFIX,
  CAMPOS_INTERV_SUFFIX,
  CI2_GROUPS,
  categorizeCondicaoRow,
  TOOLTIP_ATUALIZA_SLA,
  TOOLTIP_INSTALA_EQUIP,
  TOOLTIP_DESINSTALA_EQUIP,
  TOOLTIP_NAO_LIGA,
  TOOLTIP_SEM_PENDENCIA,
  TOOLTIP_TIPO_OS,
} from './condicoesIntervShared';
import './CondicoesIntervencaoTableV2.css';

function countByCategory(rows) {
  const counts = { pedido: 0, retorno: 0, nada: 0, desabilitada: 0 };
  rows.forEach((row) => {
    const cat = categorizeCondicaoRow(row);
    counts[cat] = (counts[cat] || 0) + 1;
  });
  return counts;
}

export function CondicoesIntervencaoTableV2({
  rows,
  totalRows,
  api,
  pecaDispHelpOpen,
  setPecaDispHelpOpen,
  pecaDispHeaderRef,
  atualizaSlaHelpOpen,
  setAtualizaSlaHelpOpen,
  atualizaSlaHeaderRef,
  installHelpOpen,
  setInstallHelpOpen,
  installHeaderRef,
  uninstallHelpOpen,
  setUninstallHelpOpen,
  uninstallHeaderRef,
  naoLigaHelpOpen,
  setNaoLigaHelpOpen,
  naoLigaHeaderRef,
  semPendenciaHelpOpen,
  setSemPendenciaHelpOpen,
  semPendenciaHeaderRef,
  tipoOsHelpOpen,
  setTipoOsHelpOpen,
  tipoOsHeaderRef,
}) {
  const [collapsedGroups, setCollapsedGroups] = useState({ desabilitada: true });

  const counts = useMemo(() => countByCategory(rows), [rows]);

  const groupedRows = useMemo(() => {
    const map = {};
    CI2_GROUPS.forEach((g) => {
      map[g.key] = rows.filter((r) => categorizeCondicaoRow(r) === g.key);
    });
    return map;
  }, [rows]);

  const visibleColCount = useMemo(() => {
    let n = 2;
    if (api.isIntervColVis('codigo')) n += 1;
    n += CAMPOS_INTERV_PREFIX.filter((c) => api.isIntervColVis(c.key)).length;
    if (api.isIntervColVis('defeito')) n += 1;
    n += CAMPOS_INTERV_SUFFIX.filter((c) => api.isIntervColVis(c.key)).length;
    if (api.isIntervColVis('atualiza_sla')) n += 1;
    if (api.isIntervColVis('func_install')) n += 1;
    if (api.isIntervColVis('func_uninstall')) n += 1;
    if (api.isIntervColVis('func_counters')) n += 1;
    if (api.isIntervColVis('func_nao_liga')) n += 1;
    if (api.isIntervColVis('func_sem_pendencia')) n += 1;
    if (api.isIntervColVis('tipo_os')) n += 1;
    return n;
  }, [api]);

  const toggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const groupsWithRows = useMemo(
    () => CI2_GROUPS.filter((g) => (groupedRows[g.key]?.length ?? 0) > 0),
    [groupedRows]
  );

  const allCollapsed = useMemo(
    () => groupsWithRows.length > 0 && groupsWithRows.every((g) => !!collapsedGroups[g.key]),
    [groupsWithRows, collapsedGroups]
  );

  const toggleAllGroups = useCallback(() => {
    const nextAllCollapsed = !allCollapsed;
    const nextCollapsed = {};
    groupsWithRows.forEach((g) => {
      nextCollapsed[g.key] = nextAllCollapsed;
    });
    setCollapsedGroups(nextCollapsed);
  }, [allCollapsed, groupsWithRows]);

  const lastHelpColumnKey = useMemo(() => {
    if (api.isIntervColVis('func_sem_pendencia')) return 'sem_pendencia';
    if (api.isIntervColVis('func_nao_liga')) return 'nao_liga';
    if (api.isIntervColVis('func_uninstall')) return 'uninstall';
    if (api.isIntervColVis('func_install')) return 'install';
    if (api.isIntervColVis('atualiza_sla')) return 'sla';
    if (CAMPOS_INTERV_SUFFIX.some((c) => c.tooltipIcon && api.isIntervColVis(c.key))) return 'peca';
    return null;
  }, [api]);

  const helpPopoverClass = (key) =>
    `ci2-th-help-popover${lastHelpColumnKey === key ? ' ci2-th-help-popover--align-end' : ''}`;

  const isGroupCollapsed = (key) => !!collapsedGroups[key];

  const closeOtherColumnHelps = useCallback(
    (except) => {
      if (except !== 'peca') setPecaDispHelpOpen(false);
      if (except !== 'sla') setAtualizaSlaHelpOpen(false);
      if (except !== 'install') setInstallHelpOpen(false);
      if (except !== 'uninstall') setUninstallHelpOpen(false);
      if (except !== 'nao_liga') setNaoLigaHelpOpen(false);
      if (except !== 'sem_pendencia') setSemPendenciaHelpOpen(false);
      if (except !== 'tipo_os') setTipoOsHelpOpen(false);
    },
    [setPecaDispHelpOpen, setAtualizaSlaHelpOpen, setInstallHelpOpen, setUninstallHelpOpen, setNaoLigaHelpOpen, setSemPendenciaHelpOpen, setTipoOsHelpOpen]
  );

  if (!rows.length && totalRows === 0) {
    return (
      <div className="ci2-root">
        <div className="ci2-panel">
          <div className="ci2-foot-note">Nenhuma condição encontrada.</div>
        </div>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="ci2-root">
        <div className="ci2-panel">
          <div className="ci2-foot-note">Nenhum resultado para a pesquisa.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ci2-root">
      <div className="ci2-panel">
        <div className="ci2-table-scroll">
          <table className="ci2-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Nome</th>
                {api.isIntervColVis('tipo_os') ? (
                  <th className={tipoOsHelpOpen ? 'ci2-th-has-help-open' : ''}>
                    <span className="ci2-th-inner ci2-th-has-help" ref={tipoOsHeaderRef}>
                      Tipo de OS
                      <Ci2HelpHexIcon
                        label="Ajuda sobre Tipo de OS"
                        expanded={tipoOsHelpOpen}
                        onToggle={() => {
                          closeOtherColumnHelps('tipo_os');
                          setTipoOsHelpOpen((v) => !v);
                        }}
                      />
                      {tipoOsHelpOpen ? (
                        <div className={helpPopoverClass('tipo_os')} role="tooltip">
                          {TOOLTIP_TIPO_OS}
                        </div>
                      ) : null}
                    </span>
                  </th>
                ) : null}
                {api.isIntervColVis('codigo') ? <th>Código</th> : null}
                {CAMPOS_INTERV_PREFIX.filter((c) => api.isIntervColVis(c.key)).map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
                {api.isIntervColVis('defeito') ? <th>{api.nativeMode ? 'Serviços' : 'Defeito'}</th> : null}
                {CAMPOS_INTERV_SUFFIX.filter((c) => api.isIntervColVis(c.key)).map((c) =>
                  c.tooltipIcon && c.tooltip ? (
                    <th key={c.key} className={pecaDispHelpOpen ? 'ci2-th-has-help-open' : ''}>
                      <span className="ci2-th-inner ci2-th-has-help" ref={pecaDispHeaderRef}>
                        {c.label}
                        <Ci2HelpHexIcon
                          label="Ajuda sobre Status peça Disp."
                          expanded={pecaDispHelpOpen}
                          onToggle={() => {
                            closeOtherColumnHelps('peca');
                            setPecaDispHelpOpen((v) => !v);
                          }}
                        />
                        {pecaDispHelpOpen ? (
                          <div className={helpPopoverClass('peca')} role="tooltip">
                            {c.tooltip}
                          </div>
                        ) : null}
                      </span>
                    </th>
                  ) : (
                    <th key={c.key}>{c.label}</th>
                  )
                )}
                {api.isIntervColVis('atualiza_sla') ? (
                  <th className={atualizaSlaHelpOpen ? 'ci2-th-has-help-open' : ''}>
                    <span className="ci2-th-inner ci2-th-has-help" ref={atualizaSlaHeaderRef}>
                      Atualiza SLA
                      <Ci2HelpHexIcon
                        label="Ajuda sobre Atualiza SLA"
                        expanded={atualizaSlaHelpOpen}
                        onToggle={() => {
                          closeOtherColumnHelps('sla');
                          setAtualizaSlaHelpOpen((v) => !v);
                        }}
                      />
                      {atualizaSlaHelpOpen ? (
                        <div className={helpPopoverClass('sla')} role="tooltip">
                          {TOOLTIP_ATUALIZA_SLA}
                        </div>
                      ) : null}
                    </span>
                  </th>
                ) : null}
                {api.isIntervColVis('func_install') ? (
                  <th className={installHelpOpen ? 'ci2-th-has-help-open' : ''}>
                    <span className="ci2-th-inner ci2-th-has-help" ref={installHeaderRef}>
                      Instala equip.
                      <Ci2HelpHexIcon
                        label="Ajuda sobre Instala equip."
                        expanded={installHelpOpen}
                        onToggle={() => {
                          closeOtherColumnHelps('install');
                          setInstallHelpOpen((v) => !v);
                        }}
                      />
                      {installHelpOpen ? (
                        <div className={helpPopoverClass('install')} role="tooltip">
                          {TOOLTIP_INSTALA_EQUIP}
                        </div>
                      ) : null}
                    </span>
                  </th>
                ) : null}
                {api.isIntervColVis('func_uninstall') ? (
                  <th className={uninstallHelpOpen ? 'ci2-th-has-help-open' : ''}>
                    <span className="ci2-th-inner ci2-th-has-help" ref={uninstallHeaderRef}>
                      Desinstala equip.
                      <Ci2HelpHexIcon
                        label="Ajuda sobre Desinstala equip."
                        expanded={uninstallHelpOpen}
                        onToggle={() => {
                          closeOtherColumnHelps('uninstall');
                          setUninstallHelpOpen((v) => !v);
                        }}
                      />
                      {uninstallHelpOpen ? (
                        <div className={helpPopoverClass('uninstall')} role="tooltip">
                          {TOOLTIP_DESINSTALA_EQUIP}
                        </div>
                      ) : null}
                    </span>
                  </th>
                ) : null}
                {api.isIntervColVis('func_counters') ? <th>Notifica contadores</th> : null}
                {api.isIntervColVis('func_nao_liga') ? (
                  <th className={naoLigaHelpOpen ? 'ci2-th-has-help-open' : ''}>
                    <span className="ci2-th-inner ci2-th-has-help" ref={naoLigaHeaderRef}>
                      Não liga
                      <Ci2HelpHexIcon
                        label="Ajuda sobre Não liga"
                        expanded={naoLigaHelpOpen}
                        onToggle={() => {
                          closeOtherColumnHelps('nao_liga');
                          setNaoLigaHelpOpen((v) => !v);
                        }}
                      />
                      {naoLigaHelpOpen ? (
                        <div className={helpPopoverClass('nao_liga')} role="tooltip">
                          {TOOLTIP_NAO_LIGA}
                        </div>
                      ) : null}
                    </span>
                  </th>
                ) : null}
                {api.isIntervColVis('func_sem_pendencia') ? (
                  <th className={semPendenciaHelpOpen ? 'ci2-th-has-help-open' : ''}>
                    <span className="ci2-th-inner ci2-th-has-help" ref={semPendenciaHeaderRef}>
                      Sem pendência
                      <Ci2HelpHexIcon
                        label="Ajuda sobre Sem pendência"
                        expanded={semPendenciaHelpOpen}
                        onToggle={() => {
                          closeOtherColumnHelps('sem_pendencia');
                          setSemPendenciaHelpOpen((v) => !v);
                        }}
                      />
                      {semPendenciaHelpOpen ? (
                        <div className={helpPopoverClass('sem_pendencia')} role="tooltip">
                          {TOOLTIP_SEM_PENDENCIA}
                        </div>
                      ) : null}
                    </span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {CI2_GROUPS.map((group) => {
                const groupRows = groupedRows[group.key] || [];
                if (!groupRows.length) return null;
                const collapsed = isGroupCollapsed(group.key);
                const dotStyle =
                  group.key === 'desabilitada'
                    ? { border: `1.5px dashed ${group.color}`, background: 'transparent' }
                    : { background: group.color };

                return (
                  <React.Fragment key={group.key}>
                    <tr
                      className={`ci2-group-row${collapsed ? ' collapsed' : ''}`}
                      onClick={() => toggleGroup(group.key)}
                    >
                      <td colSpan={visibleColCount}>
                        <div className="ci2-group-label">
                          <span className="ci2-chev">
                            <ChevronDown size={13} strokeWidth={2.5} />
                          </span>
                          <span className="ci2-dot" style={dotStyle} />
                          {group.label}
                          <span className="ci2-group-count">· {groupRows.length}</span>
                          <span className="ci2-group-desc">{group.desc}</span>
                        </div>
                      </td>
                    </tr>
                    {groupRows.map((row) => (
                      <CondicoesIntervTableRowV2
                        key={row.TB01055_CODIGO}
                        row={row}
                        cat={group.key}
                        api={api}
                        hidden={collapsed}
                      />
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="ci2-foot-note">
          <span>
            {rows.length} condição{rows.length !== 1 ? 'ões' : ''} · {CI2_GROUPS.filter((g) => groupedRows[g.key]?.length).length} grupos
          </span>
          <button
            type="button"
            className="board-clickup-filter-chip ci2-foot-toggle"
            onClick={toggleAllGroups}
            aria-label={allCollapsed ? 'Expandir todos os grupos' : 'Recolher todos os grupos'}
          >
            <span className="board-clickup-filter-chip-text">
              <span className="board-clickup-filter-chip-label">
                {allCollapsed ? 'Expandir grupos' : 'Recolher grupos'}
              </span>
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
